import { useAnalytics } from "@codat/analytics";
import {
  ClipboardCopy,
  EntityInfo,
  HighlightText,
  PencilIcon,
  useToast,
} from "@codat/orchard-ui";
import classNames from "classnames";
import React from "react";

import Button from "../../../../components/Button";
import styles from "../CompaniesTable/CompaniesTable.module.scss";

export type CompanyCellModel = {
  key: "company";
  value: {
    name: string;
    guid: string;
    onEditButtonClick: () => void;
    userCanUpdate: boolean;
    searchQuery?: string;
    hideSubtitle?: boolean;
  };
};

export function CompanyCell({ value }: Pick<CompanyCellModel, "value">) {
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();

  const title = value.userCanUpdate ? (
    <>
      <HighlightText search={value.searchQuery}>{value.name}</HighlightText>
      <Button
        label="Edit company name"
        data-testid="companies-table-edit-button"
        buttonName="Edit Company Name"
        eventProperties={{ companyId: value.guid }}
        className={classNames(styles.editButton, styles.hover)}
        onClick={(event) => {
          value.onEditButtonClick();
          event.stopPropagation();
        }}
        size="extra-small"
        variant="tertiary"
      >
        <PencilIcon className={styles.editIcon} />
      </Button>
    </>
  ) : (
    <HighlightText search={value.searchQuery}>{value.name}</HighlightText>
  );

  const onCopy = () => {
    addToast({ message: "Copied company ID to clipboard", variant: "success" });
  };

  const subtitle = !value.hideSubtitle && (
    <div className={styles.companyIdWrapper}>
      <HighlightText search={value.searchQuery}>{value.guid}</HighlightText>
      <ClipboardCopy
        className={styles.companyId}
        label="Copy company id"
        data-testid="companies-table-copy-id-button"
        iconClassName={classNames(styles.copyButton, styles.hover)}
        onCopy={onCopy}
        onClick={(e) => {
          e.stopPropagation();
          sendEvent("Company ID - Copy", { companyId: value.guid });
        }}
        noBackground
        text={value.guid}
        color="interface"
      />
    </div>
  );

  return (
    <EntityInfo
      title={title}
      subtitle={subtitle}
      data-testid="companies-table-name"
    />
  );
}
